import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { ActionRegistry } from '@packages/table-builder';
import type { ActionIntent } from '@packages/table-builder';

type ModalEntry = {
  Component: React.LazyExoticComponent<React.ComponentType<any>>;
  payload: Record<string, unknown>;
  key: string;
};

const modalLoaders: Record<string, () => Promise<{ default: React.ComponentType<any> }>> = {
  // ── Participant ─────────────────────────────────────────────────
  VIEW_PARTICIPANT: () => import('@apps/customer/domains/participant/ViewParticipant'),
  EDIT_PARTICIPANT: () => import('@apps/customer/domains/participant/EditParticipant'),
  TERMINATE_PARTICIPANT: () => import('@apps/customer/domains/participant/TerminateParticipant'),
  VIEW_TERMINATION_PARTICIPANT: () => import('@apps/customer/domains/participant/TerminateParticipant'),

  // ── Securities ──────────────────────────────────────────────────
  VIEW_SECURITIES: () => import('@apps/customer/domains/securities/ViewSecurity'),
  EDIT_SECURITIES: () => import('@apps/customer/domains/securities/EditSecurity'),
  ADD_SECURITIES: () => import('@apps/customer/domains/securities/EditSecurity'),

  // ── Warrants ────────────────────────────────────────────────────
  VIEW_WARRANTS: () => import('@apps/customer/domains/warrants/ViewWarrant'),

  // ── Convertibles ──────────────────────────────────────────────
  VIEW_CONVERTIBLE: () => import('@apps/customer/domains/convertible/ViewConvertible'),

  // ── Charter & Stock Classes ───────────────────────────────────
  VIEW_CHARTER_STOCK_CLASS: () => import('@apps/customer/domains/charter-stock-class/ViewCharterStockClass'),
  FILE_INITIAL_CHARTER: () => import('@apps/customer/domains/charter-stock-class/FileInitialCharter'),
  AMEND_CHARTER: () => import('@apps/customer/domains/charter-stock-class/AmendCharter'),
  EDIT_CHARTER: () => import('@apps/customer/domains/charter-stock-class/EditCharter'),
  IMPORT_CHARTER: () => import('@apps/customer/domains/charter-stock-class/ImportCharter'),

  // ── Valuations ──────────────────────────────────────────────
  VIEW_VALUATION: () => import('@apps/customer/domains/valuation/ViewValuation'),
  EDIT_VALUATION: () => import('@apps/customer/domains/valuation/EditValuation'),
  ADD_VALUATION: () => import('@apps/customer/domains/valuation/EditValuation'),
};

let intentCounter = 0;

/**
 * Mediator component — subscribes to ActionRegistry intents and lazy-loads
 * the appropriate modal. Mounted once in CustomerAppShell.
 *
 * Two-phase close: sets `isOpen` to false (triggers CSS exit transition),
 * then unmounts via `onExitComplete` from the modal — the modal owns its
 * animation lifecycle, not this handler.
 */
export function ActionIntentHandler() {
  const [activeModal, setActiveModal] = useState<ModalEntry | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleIntent = useCallback((intent: ActionIntent) => {
    if (intent.type !== 'OPEN_MODAL') return;

    const loader = modalLoaders[intent.actionId];
    if (!loader) {
      console.warn(`[ActionIntentHandler] No modal loader for "${intent.actionId}"`);
      return;
    }

    const Component = lazy(loader);
    intentCounter += 1;

    setActiveModal({
      Component,
      payload: intent.payload,
      key: `${intent.actionId}-${intentCounter}`,
    });
    setIsOpen(true);
  }, []);

  useEffect(() => {
    return ActionRegistry.onIntent(handleIntent);
  }, [handleIntent]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleExitComplete = useCallback(() => {
    setActiveModal(null);
  }, []);

  if (!activeModal) return null;

  const { Component, payload, key } = activeModal;

  return (
    <Suspense fallback={null}>
      <Component
        key={key}
        {...payload}
        onClose={handleClose}
        onExitComplete={handleExitComplete}
        isOpen={isOpen}
      />
    </Suspense>
  );
}
